/**
 * TrustSecurityPage — dedicated /trust-security route.
 *
 * Replaces the prior AnchorRedirect → /about#values. Ships its own
 * crawlable HTML with route-specific SEO so the search footprint
 * covers "NoJo Enterprise trust", "NoJo Enterprise security",
 * "customer data" and "audit" queries.
 *
 * Content discipline:
 *   - Reuses the five canonical operating principles from AboutPage.
 *   - Trust commitments are factual and already published in PRD.md
 *     (audit-log-ready mutations, transparency notifications,
 *      minimal data retention, no commercial data sharing).
 *   - No invented certifications, audit reports or compliance claims.
 */
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Eye,
  FileLock,
  HeartHandshake,
  Languages,
  Lock,
  Mail,
  ScrollText,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";

import Breadcrumb from "@/components/Breadcrumb";
import Reveal from "@/components/motion/Reveal";
import SEO, { PAGE_SEO } from "@/components/SEO";
import PageSideAura from "@/components/motion/PageSideAura";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";

// Five canonical operating principles — sourced from AboutPage values block.
const VALUES = [
  {
    Icon: HeartHandshake,
    accent: "#9333EA",
    titleEn: "Start from operations",
    titleJa: "実務から考える",
    bodyEn: "Design begins with what users actually struggle with on the ground.",
    bodyJa: "現場で何が困っているかから設計を始めます。",
  },
  {
    Icon: Eye,
    accent: "#2563EB",
    titleEn: "Make things understandable",
    titleJa: "わかりやすく伝える",
    bodyEn: "Hard systems translated into clear language and clear screens.",
    bodyJa: "難しい仕組みは、わかりやすい言葉と画面に。",
  },
  {
    Icon: ShieldCheck,
    accent: "#16A34A",
    titleEn: "Protect trust",
    titleJa: "信頼を守る",
    bodyEn:
      "Sensitive information stays inside secure screens, not casual channels.",
    bodyJa: "大切な情報は、安全な画面の中だけで扱います。",
  },
  {
    Icon: Languages,
    accent: "#4F46E5",
    titleEn: "Support every language",
    titleJa: "多言語で支える",
    bodyEn:
      "Japanese and English support for everyday procedures.",
    bodyJa: "日本語と英語で、お客様の手続きを支援します。",
  },
  {
    Icon: TrendingUp,
    accent: "#9333EA",
    titleEn: "Improve long-term",
    titleJa: "長期的に改善する",
    bodyEn: "We choose lasting value over short-term flash.",
    bodyJa: "短期の派手さよりも、長く続く価値を選びます。",
  },
];

// Published trust commitments — sourced from PRD.md operating gates.
const COMMITMENTS = [
  {
    Icon: Lock,
    accent: "#9333EA",
    labelEn: "We never sell your data, and we never share it outside our company",
    labelJa: "お客様のデータを販売することはなく、社外と共有することもありません",
    bodyEn:
      "Across our services — JapanETCcard, JapanTollReceipts, and JapanChangeMoney — your account works interchangeably because it's one team and one company behind all three. Inside the NoJo Enterprise organization, we consolidate your information across services so we can provide you the best possible product and support. Outside our organization, your data goes nowhere: we never sell it, and we never share it externally for any reason.",
    bodyJa:
      "JapanETCcard、JapanTollReceipts、JapanChangeMoney ─ 私たちの提供する3つのサービスは、同じチーム・同じ会社が運営しており、お客様のアカウントはサービスをまたいでご利用いただけます。NoJo Enterprise社内では、お客様により良い製品とサポートをご提供するために、サービス間で必要な情報を連携・整理しています。社外には、いかなる理由でもお客様のデータを共有することはありません。販売することも、外部に提供することも、決してありません。",
  },
  {
    Icon: ScrollText,
    accent: "#2563EB",
    labelEn: "Audit-log discipline on every mutation",
    labelJa: "すべての変更操作に監査ログ",
    bodyEn:
      "Account, billing and security events are designed to be audit-log-ready from day one — so we can answer 'what happened, when, and by whom' on request.",
    bodyJa:
      "アカウント・課金・セキュリティに関わる操作は、設計の段階から監査ログ前提で構築。「いつ、誰が、何をしたか」を後から正確に追跡できるようにしています。",
  },
  {
    Icon: FileLock,
    accent: "#16A34A",
    labelEn: "Minimal data retention",
    labelJa: "最小限のデータ保持",
    bodyEn:
      "We retain only what we need to operate the service and meet our legal obligations — no longer. Records are purged on the documented schedule.",
    bodyJa:
      "サービス運営および法令遵守に必要な期間のみデータを保持します。文書化された保持スケジュールに従って、不要となったデータは削除されます。",
  },
  {
    Icon: CheckCircle2,
    accent: "#4F46E5",
    labelEn: "Transparent reporting",
    labelJa: "透明性のある報告",
    bodyEn:
      "Public updates, roadmap status and source-freshness indicators are published openly. Internal plans and sensitive operational details stay internal.",
    bodyJa:
      "公開更新、ロードマップの進捗、データソースの鮮度を公開しています。内部計画や機微情報は社内のみで取り扱います。",
  },
];

export default function TrustSecurityPage({ lang }) {
  const isJa = lang === "ja";

  return (
    <article data-testid="trust-security-page" className="bg-white">
      <SEO
        {...PAGE_SEO.trustSecurity[lang]}
        path={`/${lang}/trust-security`}
        lang={lang}
      />
      <JsonLd
        data={[
          breadcrumbSchema([
            { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
            {
              name: isJa ? "信頼とセキュリティ" : "Trust & Security",
              url: `/${lang}/trust-security`,
            },
          ]),
        ]}
      />
      <Breadcrumb
        lang={lang}
        crumbs={[
          { label: isJa ? "信頼とセキュリティ" : "Trust & Security" },
        ]}
      />

      {/* HERO */}
      <section className="relative isolate bg-white overflow-hidden">
        <PageSideAura testId="trust-hero-aura" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-8 md:pt-14 pb-10 md:pb-14">
          <Reveal as="div" variant="up">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] font-mono-display text-[#9333EA] font-bold uppercase">
              <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2} />
              {isJa ? "信頼とセキュリティ" : "Trust & Security"}
            </div>
            <h1
              className={[
                "mt-3",
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-3xl md:text-4xl lg:text-[44px] tracking-tighter font-bold leading-[1.08] max-w-3xl",
              ].join(" ")}
              data-testid="trust-hero-title"
            >
              {isJa
                ? "ひとつのチーム、ひとつの会社で運営。サービスをまたいでベストな体験を。"
                : "One team. One company. A single account experience across our services."}
            </h1>
            <p className="mt-5 text-[15px] md:text-[16px] leading-[1.85] text-[#475569] max-w-2xl">
              {isJa
                ? "NoJo Enterpriseでは、お客様のアカウントを3つのサービスをまたいで「ひとつのご縁」として捉えています。社内では、より良い製品とサポートをご提供するため必要な情報を連携・整理する一方、お客様のデータを販売することは決してなく、社外と共有することもありません。透明性のある報告、機微情報の慎重な取扱い、最小限のデータ保持を、運用のデフォルトに組み込んでいます。"
                : "NoJo Enterprise treats every account as a single relationship across our three services. Inside our organization we consolidate the information needed to give you the best possible product and support, but we never sell your data and never share it outside our company. Transparent reporting, careful handling of sensitive information, and minimal data retention are built into our defaults."}
            </p>
          </Reveal>
        </div>
      </section>

      {/* FIVE VALUES */}
      <section
        id="values"
        data-testid="trust-values"
        className="bg-[#F8F9FA] border-y border-[#E2E8F0]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#16A34A] uppercase font-bold mb-3">
            {isJa ? "5つの価値" : "Operating Principles"}
          </div>
          <h2
            className={[
              "mb-10",
              isJa ? "font-ja" : "font-display",
              "text-[#0F1D2D] text-3xl md:text-4xl font-bold tracking-tight leading-[1.15] max-w-2xl",
            ].join(" ")}
          >
            {isJa
              ? "私たちが大切にする5つの姿勢。"
              : "Five operating principles we hold to."}
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map((v, i) => (
              <Reveal
                as="li"
                key={i}
                variant="up"
                delay={i * 90}
                data-testid={`trust-value-${i + 1}`}
                className="bg-white rounded-2xl border border-[#E2E8F0] p-5 md:p-6 hover:-translate-y-0.5 transition-transform"
              >
                <div className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="inline-flex items-center justify-center h-10 w-10 rounded-md shrink-0"
                    style={{ backgroundColor: `${v.accent}15` }}
                  >
                    <v.Icon
                      className="h-5 w-5"
                      strokeWidth={1.7}
                      style={{ color: v.accent }}
                    />
                  </span>
                  <div className="min-w-0">
                    <div
                      className="font-mono-display text-[10px] tracking-[0.22em] font-bold uppercase"
                      style={{ color: v.accent }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3
                      className={[
                        "mt-1",
                        isJa ? "font-ja" : "font-display",
                        "text-[#0F1D2D] text-[17px] font-bold tracking-tight leading-snug",
                      ].join(" ")}
                    >
                      {isJa ? v.titleJa : v.titleEn}
                    </h3>
                    <p className="mt-2 text-[13.5px] leading-[1.8] text-[#475569]">
                      {isJa ? v.bodyJa : v.bodyEn}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* COMMITMENTS */}
      <section data-testid="trust-commitments" className="relative isolate bg-white">
        <PageSideAura testId="trust-commitments-aura" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#2563EB] uppercase font-bold mb-3">
            {isJa ? "私たちのお約束" : "Our Published Commitments"}
          </div>
          <h2
            className={[
              "mb-10",
              isJa ? "font-ja" : "font-display",
              "text-[#0F1D2D] text-3xl md:text-4xl font-bold tracking-tight leading-[1.15] max-w-2xl",
            ].join(" ")}
          >
            {isJa
              ? "お客様データに対する、明文化された運用方針。"
              : "Documented operating posture on customer data."}
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {COMMITMENTS.map((c, i) => (
              <Reveal
                as="li"
                key={c.labelEn}
                variant="up"
                delay={i * 90}
                data-testid={`trust-commitment-${i}`}
                className="bg-white rounded-2xl border border-[#E2E8F0] p-5 md:p-6"
              >
                <span
                  aria-hidden
                  className="inline-flex items-center justify-center h-10 w-10 rounded-md"
                  style={{ backgroundColor: `${c.accent}15` }}
                >
                  <c.Icon
                    className="h-5 w-5"
                    strokeWidth={1.7}
                    style={{ color: c.accent }}
                  />
                </span>
                <h3
                  className={[
                    "mt-3",
                    isJa ? "font-ja" : "font-display",
                    "text-[#0F1D2D] text-[17px] font-bold tracking-tight leading-snug",
                  ].join(" ")}
                >
                  {isJa ? c.labelJa : c.labelEn}
                </h3>
                <p className="mt-2 text-[14px] leading-[1.85] text-[#475569]">
                  {isJa ? c.bodyJa : c.bodyEn}
                </p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="trust-cta" className="relative isolate overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, #6D28D9 0%, #9333EA 35%, #C026D3 65%, #9333EA 100%)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-25"
          style={{
            background:
              "radial-gradient(900px 320px at 20% 0%, rgba(255,255,255,0.22), transparent 60%), radial-gradient(700px 280px at 80% 100%, rgba(255,255,255,0.14), transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 text-white">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="text-[10px] tracking-[0.28em] font-mono-display text-white/85 uppercase font-bold">
                {isJa ? "ご相談ください" : "Get in touch"}
              </div>
              <h2
                className={[
                  "mt-3",
                  isJa ? "font-ja" : "font-display",
                  "text-white text-2xl md:text-3xl font-bold tracking-tight leading-[1.2]",
                ].join(" ")}
              >
                {isJa
                  ? "お客様データの取り扱いについて、ご質問はいつでもどうぞ。"
                  : "Questions about how we handle your data? We welcome them."}
              </h2>
              <p className="mt-4 text-[14.5px] leading-[1.85] text-white/90 max-w-2xl">
                {isJa
                  ? "個別のお問い合わせには、日本語または英語で営業日24時間以内に返信いたします。"
                  : "We reply to individual inquiries in Japanese or English within 24 hours on business days."}
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
              <Link
                to={`/${lang}/contact`}
                data-testid="trust-cta-contact"
                className="inline-flex items-center gap-2 bg-white text-[#6D28D9] px-5 h-12 rounded-full text-[13px] font-semibold tracking-tight hover:bg-[#F5F3FF] transition-colors"
              >
                <Mail className="h-4 w-4" strokeWidth={2.25} />
                {isJa ? "お問い合わせ" : "Contact us"}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
              </Link>
              <Link
                to={`/${lang}/legal/privacy`}
                data-testid="trust-cta-privacy"
                className="inline-flex items-center gap-2 ring-1 ring-white/50 text-white px-5 h-12 rounded-full text-[13px] font-semibold tracking-tight hover:bg-white/15 transition-colors"
              >
                {isJa ? "プライバシーポリシー" : "Privacy Policy"}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
